import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, errMsg } from "../../lib/api";
import { CATS, catLabel } from "../Gallery";

const UploadForm = ({ onDone }) => {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("unghie");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category); fd.append("caption", caption);
    setBusy(true);
    try { await api.post("/admin/gallery", fd); toast.success("Foto caricata"); setFile(null); setCaption(""); e.target.reset(); onDone(); }
    catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="admin-upload-form" className="grid gap-4 rounded-[2rem] border border-[var(--gold)]/25 bg-white/60 p-6 md:grid-cols-[1.4fr_1fr_1.4fr_auto] md:items-end">
      <label className="text-sm">Foto
        <input data-testid="admin-upload-file" type="file" accept="image/jpeg,image/png,image/webp" required onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-2 block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[var(--blush)] file:px-4 file:py-2" />
      </label>
      <label className="text-sm">Categoria
        <select data-testid="admin-upload-category" value={category} onChange={(e) => setCategory(e.target.value)} className="mt-2 block w-full rounded-xl border border-[var(--ink)]/15 bg-white px-3 py-2.5">
          {CATS.filter(([k]) => k !== "tutti").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      <label className="text-sm">Didascalia (facoltativa)
        <input data-testid="admin-upload-caption" maxLength={140} value={caption} onChange={(e) => setCaption(e.target.value)} className="mt-2 block w-full rounded-xl border border-[var(--ink)]/15 bg-white px-3 py-2.5" />
      </label>
      <button data-testid="admin-upload-submit" disabled={busy || !file} className="flex items-center justify-center gap-2 rounded-full bg-[var(--ink)] px-6 py-3 text-sm text-[var(--cream)] hover:bg-[var(--gold-deep)] disabled:opacity-50"><Upload size={15} />{busy ? "Caricamento..." : "Carica"}</button>
    </form>
  );
};

export const AdminGallery = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["gallery"], queryFn: async () => (await api.get("/gallery")).data });
  const photos = Array.isArray(data) ? data : [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["gallery"] });

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa foto dalla galleria?")) return;
    try { await api.delete(`/admin/gallery/${id}`); toast.success("Foto eliminata"); refresh(); } catch (x) { toast.error(errMsg(x)); }
  };

  return (
    <div className="space-y-8">
      <UploadForm onDone={refresh} />
      {photos.length === 0 && <p data-testid="admin-gallery-empty" className="text-[var(--ink-soft)]">Nessuna foto caricata. Finché la galleria è vuota, sul sito vengono mostrate immagini dimostrative.</p>}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {photos.map((p) => (
          <div key={p.id} data-testid={`admin-photo-${p.id}`} className="group relative aspect-square overflow-hidden rounded-2xl bg-[var(--blush)]">
            <img src={imgUrl(p.url)} alt={p.caption} className="h-full w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white">
              <p className="text-[11px] uppercase tracking-widest">{catLabel(p.category)}</p>
              {p.caption && <p className="truncate text-sm">{p.caption}</p>}
            </div>
            <button data-testid={`admin-photo-delete-${p.id}`} onClick={() => remove(p.id)} aria-label="Elimina" className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-red-700 shadow hover:bg-white"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
